import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { storeAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Loader2, Download, Clock, AlertTriangle, ShieldCheck, Bookmark, Link2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ThankYouPage() {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingToken, setDownloadingToken] = useState(null);

  const loadData = async () => {
    try {
      const res = await storeAPI.thankYou(sessionId);
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'This page is no longer available.' : 'Failed to load your order.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [sessionId]);

  const handleDownload = async (token) => {
    setDownloadingToken(token);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${backendUrl}/api/download/${token}`, '_blank');
      setTimeout(() => {
        loadData();
        setDownloadingToken(null);
      }, 2500);
    } catch {
      toast.error('Download failed');
      setDownloadingToken(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#195259' }}>
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: '#FF9A00' }} />
          <p className="text-sm" style={{ color: '#F5E9CF' }}>Loading your order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#195259' }}>
        <Card className="w-full max-w-md" style={{ border: '1.5px solid #195259' }}>
          <CardContent className="pt-8 pb-8 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4" style={{ color: '#FF9A00' }} />
            <h2 className="text-xl font-semibold mb-2">Oops!</h2>
            <p className="text-muted-foreground text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter to only items with download tokens (skip non-downloadable items)
  const downloadableItems = data.items.filter(i => i.download_token && !i.is_bundle_item);
  const bundleChildItems = data.items.filter(i => i.download_token && i.is_bundle_item);
  const allDownloadItems = [...downloadableItems, ...bundleChildItems];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#195259' }} data-testid="thank-you-page">
      {/* Header */}
      <div className="px-4 pt-12 pb-20">
        <div className="max-w-lg mx-auto text-center space-y-3">
          <div className="flex items-center justify-center mx-auto">
            <img src="/images/thank-you-envelope.png" alt="Thank you" className="w-56 h-56 md:w-64 md:h-64 object-contain" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
            Thank You{data.customer_name ? `, ${data.customer_name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-sm max-w-sm mx-auto" style={{ color: '#F5E9CF' }}>
            Your purchase is complete. Download your files below before the links expire.
          </p>
          {data.total_amount > 0 && (
            <div className="pt-1">
              <Badge className="text-sm px-4 py-1.5 font-semibold" style={{ backgroundColor: '#FF9A00', color: '#195259' }}>
                Total: RM {data.total_amount.toFixed(2)}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 -mt-10 pb-12">
        <div className="max-w-lg mx-auto space-y-4">

        {/* Download Items */}
        {allDownloadItems.length > 0 ? (
          <div className="space-y-3">
            {allDownloadItems.map((item) => {
              const info = item.download_info;
              if (!info) return null;

              const canDownload = !info.is_expired && !info.is_limit_reached && !info.is_revoked;
              const progressPct = (info.downloads_used / info.max_downloads) * 100;
              const isDownloading = downloadingToken === item.download_token;

              return (
                <Card key={item.download_token} className="overflow-hidden" data-testid={`thank-you-item`}>
                  <CardContent className="p-0">
                    <div className="p-5 space-y-4">
                      {/* Product Info */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base" style={{ color: '#195259' }}>{item.product_name}</h3>
                          {item.order_ref && <span className="text-[10px] font-mono text-muted-foreground">{item.order_ref}</span>}
                        </div>
                        {canDownload ? (
                          <Badge className="bg-emerald-100 text-emerald-700 shrink-0">Active</Badge>
                        ) : (
                          <Badge variant="destructive" className="shrink-0">
                            {info.is_expired ? 'Expired' : info.is_limit_reached ? 'Limit Reached' : 'Revoked'}
                          </Badge>
                        )}
                      </div>

                      {/* Limits & Expiry */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(25,82,89,0.06)' }}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <Download className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Downloads</span>
                          </div>
                          <p className="text-lg font-bold text-foreground">{info.downloads_used}<span className="text-sm font-normal text-muted-foreground"> / {info.max_downloads}</span></p>
                          <Progress value={progressPct} className="h-1.5 mt-1.5" />
                        </div>
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(25,82,89,0.06)' }}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Expires In</span>
                          </div>
                          {info.is_expired ? (
                            <p className="text-lg font-bold text-destructive">Expired</p>
                          ) : info.hours_remaining > 24 ? (
                            <p className="text-lg font-bold text-foreground">{Math.floor(info.hours_remaining / 24)}<span className="text-sm font-normal text-muted-foreground"> days {Math.round(info.hours_remaining % 24)}h</span></p>
                          ) : (
                            <p className="text-lg font-bold text-amber-600">{Math.floor(info.hours_remaining)}<span className="text-sm font-normal text-muted-foreground"> hours {Math.round((info.hours_remaining % 1) * 60)}m</span></p>
                          )}
                        </div>
                      </div>

                      {/* Download Button */}
                      {canDownload ? (
                        <Button
                          className="w-full font-semibold"
                          size="lg"
                          style={{ backgroundColor: '#FF9A00', color: '#195259' }}
                          onClick={() => handleDownload(item.download_token)}
                          disabled={isDownloading}
                          data-testid="thank-you-download-btn"
                        >
                          {isDownloading ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading...</>
                          ) : (
                            <><Download className="h-4 w-4 mr-2" /> Download Now</>
                          )}
                        </Button>
                      ) : (
                        <div className="text-center py-2">
                          <p className="text-sm text-muted-foreground">This download link is no longer available.</p>
                          <p className="text-xs text-muted-foreground mt-1">Contact the creator for assistance.</p>
                        </div>
                      )}

                      {/* Course Access Link */}
                      {item.access_link && (
                        <a
                          href={item.access_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 w-full p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                          data-testid="thank-you-access-link"
                        >
                          <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                            <Link2 className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-emerald-800">Access Course</p>
                            <p className="text-xs text-emerald-600 truncate">{item.access_link}</p>
                          </div>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">Your order has been recorded. The creator will provide access to your products.</p>
            </CardContent>
          </Card>
        )}

        {/* Save Link Notice */}
        <Card className="overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.15)' }}>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <Bookmark className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#FF9A00' }} />
              <div className="space-y-2 min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">Save this page link!</p>
                <p className="text-xs leading-relaxed" style={{ color: '#F5E9CF' }}>
                  This is your <strong className="text-white">only access</strong> to download your purchased files. Bookmark this page or copy the link below and save it somewhere safe.
                  The download link will expire based on the time limit shown above.
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="min-w-0 flex-1 flex items-center gap-2 rounded-lg px-3 py-2" style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <Link2 className="h-3.5 w-3.5 shrink-0" style={{ color: '#FF9A00' }} />
                    <span className="text-[11px] font-mono truncate block" style={{ color: '#F5E9CF' }}>{window.location.href}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 font-semibold"
                    style={{ backgroundColor: '#FF9A00', color: '#195259', borderColor: '#FF9A00' }}
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied to clipboard!');
                    }}
                    data-testid="thank-you-copy-link"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center space-y-3 pt-2">
          <Separator style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          {/* Funnely Branding - shown for free/pro plans */}
          {data.creator_plan !== 'premium' && (
            <div className="flex items-center justify-center gap-1.5 pt-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <ShieldCheck className="h-3.5 w-3.5" />
              <span className="text-xs">Powered by</span>
              <img src="/logos/funnely-white.png" alt="Funnely" className="h-3.5 object-contain opacity-60" />
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
